import type { components } from '@/api/schema'
import { Badge } from '@/components/ui/badge'
import { classificationReasons } from './run-display'

// Mirrors ActionGate.eligible in backend/app/services/classification_execution.py.
const AUTOMATIC_ACTION_CONFIDENCE = 0.7

type Action = components['schemas']['ClassificationAction']

const statuses: Record<NonNullable<Action['status']>, string> = {
  pending: 'Waiting',
  started: 'In progress',
  succeeded: 'Done',
  skipped: 'Not done',
  cancelled: 'Stopped',
  unknown: 'Check your inbox',
  failed: 'Failed',
}

const reasons: Record<NonNullable<Action['reason']>, string> = {
  ...classificationReasons,
}

export function ActionOutcome({
  action,
  name,
  preview = false,
  readinessReason,
}: {
  action: Action
  name: string
  preview?: boolean
  readinessReason?: string | null
}) {
  // A deleted email isn't a failure of ours; show it as not done.
  const status =
    action.reason === 'message_missing' ? 'skipped' : (action.status ?? 'pending')
  const confidence = action.decision_probability
  const reason =
    action.reason === 'ineligible'
      ? confidence != null && confidence < AUTOMATIC_ACTION_CONFIDENCE
        ? `Needs ${AUTOMATIC_ACTION_CONFIDENCE * 100}% confidence to act automatically.`
        : 'This email didn’t call for it.'
      : action.reason === 'gate_unavailable'
        ? (readinessReason ?? classificationReasons.gate_unavailable)
        : action.reason
          ? reasons[action.reason]
          : null
  return (
    <div className="space-y-1 text-sm">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-semibold">
          {action.target_name ? `${name} “${action.target_name}”` : name}
        </span>
        <Badge
          variant={
            status === 'succeeded'
              ? 'default'
              : status === 'failed'
                ? 'destructive'
                : 'secondary'
          }
        >
          {preview && status === 'pending'
            ? 'Would do'
            : status === 'succeeded' && action.changed === false
              ? 'Already done'
              : statuses[status]}
        </Badge>
        {confidence != null && (
          <span className="text-xs text-muted-foreground tabular-nums">
            {Math.round(confidence * 100)}% confident
          </span>
        )}
      </div>
      {reason && <p className="text-muted-foreground">{reason}</p>}
    </div>
  )
}
